/** C35 图表原语 —— 自绘 SVG,零依赖;指标统计全站共用。
 *
 * 规范(dataviz 硬约束):线 2px 圆角端;柱 ≤24px、4px 圆角数据端(基线方);段间/柱间 2px
 * surface 留白;网格 hairline 实线一档灰;文字只穿 text 令牌(数据色只落在 mark 上);
 * ≥2 系必带图例;禁双轴。颜色从 @cs-agent/design-tokens 取(单源 ADR-506);
 * 分类三系 [info,accent,success] 已过 validate_palette.js(cyan 对比 WARN → 以图例+
 * 直标+tooltip 补偿,勿删)。
 */

import tokens from "@cs-agent/design-tokens";
import { type ReactNode, useId, useState } from "react";

type TokenLeaf = { value: string };
type TokenGroup = Record<string, { value?: string } | Record<string, unknown>>;
const _color = (path: string): string => {
	let cur: unknown = (tokens as { color: TokenGroup }).color;
	for (const seg of path.split(".")) {
		cur = (cur as Record<string, unknown>)?.[seg];
	}
	return (cur as TokenLeaf)?.value ?? "#8C8A85";
};

/** 图表专用色(同源 tokens):分类色固定绑定实体,筛选变化不重涂 */
export const CHART = {
	accent: _color("brand.accent"),
	success: _color("brand.success"),
	warning: _color("brand.warning"),
	danger: _color("brand.danger"),
	info: _color("brand.info"),
	ink: _color("text.primary"),
	inkSecondary: _color("text.secondary"),
	inkMuted: _color("text.muted"),
	surface: _color("surface.background"),
	grid: "#E7E5E4", // 一档灰 hairline(surface.border 同族)
} as const;

const GAP = 2; // 段间/柱间/环间 surface 留白(px)

// ---------------------------------------------------------------- Sparkline
/** 12 点迷你趋势:去噪展示,无轴无网格;末点 accent 圆点(r4 + 2px surface ring)。 */
export function Sparkline({
	points,
	width = 72,
	height = 22,
}: {
	points: (number | null)[];
	width?: number;
	height?: number;
}) {
	const vals = points.filter((v): v is number => v != null);
	if (vals.length < 2) return null;
	const min = Math.min(...vals);
	const max = Math.max(...vals);
	const span = max - min || 1;
	const pad = 4;
	const xy = points
		.map((v, i) =>
			v == null
				? null
				: ([
						pad + (i * (width - pad * 2)) / Math.max(1, points.length - 1),
						height - pad - ((v - min) / span) * (height - pad * 2),
					] as const),
		)
		.filter((p): p is readonly [number, number] => p !== null);
	const last = xy[xy.length - 1];
	return (
		<svg width={width} height={height} aria-hidden="true" className="shrink-0">
			<polyline
				points={xy.map(([x, y]) => `${x},${y}`).join(" ")}
				fill="none"
				stroke={CHART.inkMuted}
				strokeWidth={2}
				strokeLinejoin="round"
				strokeLinecap="round"
			/>
			{last ? (
				<circle
					cx={last[0]}
					cy={last[1]}
					r={4}
					fill={CHART.accent}
					stroke={CHART.surface}
					strokeWidth={GAP}
				/>
			) : null}
		</svg>
	);
}

// ---------------------------------------------------------------- DeltaBadge
/** 环比 Δ:色 = 方向 × 好坏;中性量指标不着色。 */
export function DeltaBadge({
	value,
	prev,
	goodWhen = "up",
	fmt = (n) => String(n),
}: {
	value: number | null;
	prev: number | null;
	goodWhen?: "up" | "down" | "neutral";
	fmt?: (n: number) => string;
}) {
	if (value == null || prev == null) return null;
	const diff = value - prev;
	if (diff === 0) return <span className="text-xs text-text-muted">持平</span>;
	const up = diff > 0;
	const good = goodWhen === "neutral" ? null : (goodWhen === "up") === up;
	const cls = good == null ? "text-text-muted" : good ? "text-brand-success" : "text-brand-danger";
	return (
		<span className={`whitespace-nowrap font-mono text-xs ${cls}`}>
			{up ? "▲" : "▼"} {fmt(Math.abs(diff))}
		</span>
	);
}

/** 大数字紧凑格式(stat tile 契约:1,284 / 12.9K / 4.2M),Δ 与窄卡用。 */
export function fmtCompact(n: number): string {
	const abs = Math.abs(n);
	if (abs >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
	if (abs >= 10_000) return `${(n / 1e3).toFixed(0)}K`;
	if (abs >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
	return String(Math.round(n));
}

// ---------------------------------------------------------------- StatTile
/** stat tile 契约:label + 大数值 + Δ(vs 上一同长窗口) + 12 点 sparkline。 */
export function StatTile({
	label,
	value,
	delta,
	spark,
	hint,
	testId,
}: {
	label: string;
	value: ReactNode;
	delta?: ReactNode;
	spark?: (number | null)[];
	hint?: ReactNode;
	testId?: string;
}) {
	return (
		<div
			className="rounded-xl border border-surface-border bg-surface-card p-4"
			data-testid={testId}
		>
			<div className="truncate font-mono text-[11px] uppercase tracking-wide text-text-muted">
				{label}
			</div>
			{/* 数值是主角绝不截断;窄卡放不下时 spark 换行下移(flex-wrap),数字保持完整 */}
			<div className="flex flex-wrap items-end justify-between gap-x-2 gap-y-1 pt-1">
				<div className="whitespace-nowrap text-2xl font-semibold text-text-primary">{value}</div>
				{spark ? <Sparkline points={spark} width={64} /> : null}
			</div>
			{/* Δ 与说明各自 nowrap 成块换行,绝不从内容中间断开(窄卡防错位) */}
			<div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 pt-0.5 text-xs text-text-muted">
				{delta}
				{hint ? <span className="whitespace-nowrap">{hint}</span> : null}
			</div>
		</div>
	);
}

// ---------------------------------------------------------------- TrendChart
export type TrendSeries = {
	key: string;
	label: string;
	color: string; // CHART.* hex,固定绑定实体
	points: (number | null)[];
};

/** 多系折线(同量纲单轴):2px 线 + hairline 网格 + 图例 + hover 十字线/tooltip。 */
export function TrendChart({
	dates,
	series,
	percent = false,
	height = 160,
	testId,
}: {
	dates: string[];
	series: TrendSeries[];
	percent?: boolean;
	height?: number;
	testId?: string;
}) {
	const id = useId();
	const [hover, setHover] = useState<number | null>(null);
	const width = 560;
	const padL = 34;
	const padR = 46; // 给末端直标留位,文字画在图内绝不溢出裁切
	const padT = 10;
	const padB = 20;
	const all = series.flatMap((s) => s.points).filter((v): v is number => v != null);
	if (!dates.length || !all.length) {
		return <div className="p-3 text-xs text-text-muted">该窗口暂无数据。</div>;
	}
	const yMax = percent ? 100 : Math.max(1, ...all);
	const x = (i: number) => padL + (i * (width - padL - padR)) / Math.max(1, dates.length - 1);
	const y = (v: number) => padT + (1 - v / yMax) * (height - padT - padB);
	const gridYs = [0, 0.5, 1].map((f) => f * yMax);
	const fmt = (v: number) => (percent ? `${Math.round(v)}%` : fmtCompact(v));
	// 末端直标碰撞处理:多系收敛在右缘时只标间距足够的,其余交给图例+tooltip(不硬堆)
	const lastIdxOf = (s: TrendSeries) =>
		s.points.reduce<number>((a, v, i) => (v == null ? a : i), -1);
	const endLabelKeys = (() => {
		const ends = series
			.map((s) => {
				const li = lastIdxOf(s);
				return li >= 0 ? { key: s.key, y: y(s.points[li] as number) } : null;
			})
			.filter((e): e is { key: string; y: number } => e !== null)
			.sort((a, b) => a.y - b.y);
		const keep = new Set<string>();
		let prevY = -99;
		for (const e of ends) {
			if (e.y - prevY >= 11) {
				keep.add(e.key);
				prevY = e.y;
			}
		}
		return keep;
	})();

	return (
		<div data-testid={testId}>
			<svg
				viewBox={`0 0 ${width} ${height}`}
				className="w-full"
				role="img"
				aria-label="趋势图"
				onMouseLeave={() => setHover(null)}
				onMouseMove={(e) => {
					const rect = e.currentTarget.getBoundingClientRect();
					const px = ((e.clientX - rect.left) / rect.width) * width;
					const i = Math.round(((px - padL) / (width - padL - padR)) * (dates.length - 1));
					setHover(Math.max(0, Math.min(dates.length - 1, i)));
				}}
			>
				{gridYs.map((g) => (
					<g key={g}>
						<line
							x1={padL}
							x2={width - padR}
							y1={y(g)}
							y2={y(g)}
							stroke={CHART.grid}
							strokeWidth={1}
						/>
						<text x={padL - 4} y={y(g) + 3} textAnchor="end" fontSize={9} fill={CHART.inkMuted}>
							{fmt(g)}
						</text>
					</g>
				))}
				{dates.map((d, i) =>
					i === 0 || i === dates.length - 1 || dates.length <= 4 ? (
						<text
							key={d}
							x={x(i)}
							y={height - 6}
							textAnchor="middle"
							fontSize={9}
							fill={CHART.inkMuted}
						>
							{d.slice(5)}
						</text>
					) : null,
				)}
				{hover != null ? (
					<line
						x1={x(hover)}
						x2={x(hover)}
						y1={padT}
						y2={height - padB}
						stroke={CHART.inkMuted}
						strokeWidth={1}
					/>
				) : null}
				{series.map((s) => {
					const nonNull = s.points.filter((v) => v != null).length;
					const pts = s.points
						.map((v, i) => (v == null ? null : `${x(i)},${y(v)}`))
						.filter(Boolean)
						.join(" ");
					const lastIdx = lastIdxOf(s);
					return (
						<g key={s.key}>
							<polyline
								points={pts}
								fill="none"
								stroke={s.color}
								strokeWidth={2}
								strokeLinejoin="round"
								strokeLinecap="round"
							/>
							{nonNull === 1 && lastIdx >= 0 ? (
								// 单点系列连不成线 → 画常驻 marker,保证"有值必可见"(图例才不撒谎)
								<circle
									cx={x(lastIdx)}
									cy={y(s.points[lastIdx] as number)}
									r={4}
									fill={s.color}
									stroke={CHART.surface}
									strokeWidth={GAP}
								/>
							) : null}
							{hover != null && s.points[hover] != null ? (
								<circle
									cx={x(hover)}
									cy={y(s.points[hover] as number)}
									r={4}
									fill={s.color}
									stroke={CHART.surface}
									strokeWidth={GAP}
								/>
							) : null}
							{lastIdx >= 0 && endLabelKeys.has(s.key) ? (
								// 末端直标(选择性:收敛碰撞的系列交给图例+tooltip);画在预留 padR 内不裁切
								<text
									x={x(lastIdx) + 6}
									y={y(s.points[lastIdx] as number) + 3}
									fontSize={9}
									fill={CHART.inkSecondary}
								>
									{fmt(s.points[lastIdx] as number)}
								</text>
							) : null}
						</g>
					);
				})}
			</svg>
			{/* 图例(≥2 系必带) + hover tooltip 行 */}
			<div className="flex flex-wrap items-center gap-3 pt-1 text-xs" id={id}>
				{series.map((s) => (
					<span key={s.key} className="flex items-center gap-1.5 text-text-secondary">
						<span className="h-1.5 w-3.5 rounded-full" style={{ background: s.color }} />
						{s.label}
						{hover != null ? (
							<span className="font-mono text-text-primary">
								{s.points[hover] == null ? "—" : fmt(s.points[hover] as number)}
							</span>
						) : null}
					</span>
				))}
				{hover != null ? <span className="font-mono text-text-muted">{dates[hover]}</span> : null}
			</div>
		</div>
	);
}

// ---------------------------------------------------------------- DayBars
/** 按天分组柱(计数同量纲单轴):柱 ≤24px、4px 圆角数据端、柱间 2px 留白、hover tooltip。 */
export function DayBars({
	dates,
	series,
	height = 140,
	testId,
}: {
	dates: string[];
	series: TrendSeries[];
	height?: number;
	testId?: string;
}) {
	const [hover, setHover] = useState<number | null>(null);
	const width = 560;
	const padL = 34;
	const padR = 12;
	const padT = 10;
	const padB = 20;
	const all = series.flatMap((s) => s.points).filter((v): v is number => v != null);
	if (!dates.length || !all.length) {
		return <div className="p-3 text-xs text-text-muted">该窗口暂无数据。</div>;
	}
	const yMax = Math.max(1, ...all);
	const slot = (width - padL - padR) / dates.length;
	const barW = Math.min(24, Math.max(3, (slot - GAP * series.length) / series.length - 1));
	const y = (v: number) => padT + (1 - v / yMax) * (height - padT - padB);

	return (
		<div data-testid={testId}>
			<svg
				viewBox={`0 0 ${width} ${height}`}
				className="w-full"
				role="img"
				aria-label="按天分布"
				onMouseLeave={() => setHover(null)}
				onMouseMove={(e) => {
					const rect = e.currentTarget.getBoundingClientRect();
					const px = ((e.clientX - rect.left) / rect.width) * width;
					setHover(Math.max(0, Math.min(dates.length - 1, Math.floor((px - padL) / slot))));
				}}
			>
				{[0.5, 1].map((f) => (
					<g key={f}>
						<line
							x1={padL}
							x2={width - padR}
							y1={y(f * yMax)}
							y2={y(f * yMax)}
							stroke={CHART.grid}
							strokeWidth={1}
						/>
						<text
							x={padL - 4}
							y={y(f * yMax) + 3}
							textAnchor="end"
							fontSize={9}
							fill={CHART.inkMuted}
						>
							{Math.round(f * yMax).toLocaleString("en-US")}
						</text>
					</g>
				))}
				<line x1={padL} x2={width - padR} y1={y(0)} y2={y(0)} stroke={CHART.grid} strokeWidth={1} />
				{dates.map((d, di) => {
					const groupW = series.length * (barW + GAP) - GAP;
					const x0 = padL + di * slot + (slot - groupW) / 2;
					return (
						<g key={d} opacity={hover == null || hover === di ? 1 : 0.45}>
							{series.map((s, si) => {
								const v = s.points[di];
								if (v == null || v === 0) return null;
								const h = Math.max(2, y(0) - y(v));
								return (
									<path
										key={s.key}
										d={roundedTopBar(x0 + si * (barW + GAP), y(0), barW, h)}
										fill={s.color}
									/>
								);
							})}
							{(di === 0 || di === dates.length - 1 || dates.length <= 4) && (
								<text
									x={padL + di * slot + slot / 2}
									y={height - 6}
									textAnchor="middle"
									fontSize={9}
									fill={CHART.inkMuted}
								>
									{d.slice(5)}
								</text>
							)}
						</g>
					);
				})}
			</svg>
			<div className="flex flex-wrap items-center gap-3 pt-1 text-xs">
				{series.map((s) => (
					<span key={s.key} className="flex items-center gap-1.5 text-text-secondary">
						<span className="h-2 w-2 rounded-[2px]" style={{ background: s.color }} />
						{s.label}
						{hover != null ? (
							<span className="font-mono text-text-primary">{s.points[hover] ?? "—"}</span>
						) : null}
					</span>
				))}
				{hover != null ? <span className="font-mono text-text-muted">{dates[hover]}</span> : null}
			</div>
		</div>
	);
}

/** 4px 圆角数据端 + 方形基线的柱形 path。 */
function roundedTopBar(x: number, baseY: number, w: number, h: number): string {
	const r = Math.min(4, w / 2, h);
	const top = baseY - h;
	return `M ${x} ${baseY} L ${x} ${top + r} Q ${x} ${top} ${x + r} ${top} L ${x + w - r} ${top} Q ${x + w} ${top} ${x + w} ${top + r} L ${x + w} ${baseY} Z`;
}

// ---------------------------------------------------------------- Donut
export type DonutSlice = { key: string; label: string; value: number; color: string };

/** 扇形(身份型占比,≤5 片强约束):片间 2px 留白、中心主数值、旁列图例(文字穿 text 令牌)。
 *  >maxSlices 自动折叠尾部为「其他」——绝不生成新色。 */
export function Donut({
	slices,
	center,
	centerHint,
	size = 132,
	maxSlices = 5,
	testId,
}: {
	slices: DonutSlice[];
	center: string;
	centerHint?: string;
	size?: number;
	maxSlices?: number;
	testId?: string;
}) {
	const [hover, setHover] = useState<string | null>(null);
	const sorted = [...slices].sort((a, b) => b.value - a.value).filter((s) => s.value > 0);
	const head = sorted.slice(0, maxSlices - (sorted.length > maxSlices ? 1 : 0));
	const tail = sorted.slice(head.length);
	const shown: DonutSlice[] = tail.length
		? [
				...head,
				{
					key: "__other__",
					label: `其他(${tail.length})`,
					value: tail.reduce((a, s) => a + s.value, 0),
					color: CHART.inkMuted,
				},
			]
		: head;
	const total = shown.reduce((a, s) => a + s.value, 0);
	if (!total) return <div className="p-3 text-xs text-text-muted">该窗口暂无数据。</div>;

	const cx = size / 2;
	const r = size / 2 - 4;
	const inner = r * 0.62;
	const gapAngle = shown.length > 1 ? (GAP / r) * (180 / Math.PI) : 0;
	let angle = -90;
	const arcs = shown.map((s) => {
		const sweep = (s.value / total) * 360;
		const a0 = angle + gapAngle / 2;
		const a1 = angle + sweep - gapAngle / 2;
		angle += sweep;
		return { s, a0, a1: Math.max(a0 + 0.5, a1) };
	});
	const pt = (a: number, rad: number) =>
		`${cx + rad * Math.cos((a * Math.PI) / 180)},${cx + rad * Math.sin((a * Math.PI) / 180)}`;
	const arcPath = (a0: number, a1: number) => {
		const large = a1 - a0 > 180 ? 1 : 0;
		return `M ${pt(a0, r)} A ${r} ${r} 0 ${large} 1 ${pt(a1, r)} L ${pt(a1, inner)} A ${inner} ${inner} 0 ${large} 0 ${pt(a0, inner)} Z`;
	};

	return (
		<div className="flex items-center gap-4" data-testid={testId}>
			<svg width={size} height={size} role="img" aria-label="占比图">
				{arcs.map(({ s, a0, a1 }) => (
					<path
						key={s.key}
						d={arcPath(a0, a1)}
						fill={s.color}
						opacity={hover == null || hover === s.key ? 1 : 0.4}
						onMouseEnter={() => setHover(s.key)}
						onMouseLeave={() => setHover(null)}
					/>
				))}
				<text x={cx} y={cx - 2} textAnchor="middle" fontSize={17} fontWeight={600} fill={CHART.ink}>
					{center}
				</text>
				{centerHint ? (
					<text x={cx} y={cx + 13} textAnchor="middle" fontSize={9} fill={CHART.inkMuted}>
						{centerHint}
					</text>
				) : null}
			</svg>
			<div className="min-w-0 space-y-1 text-xs">
				{shown.map((s) => (
					<div
						key={s.key}
						className={`flex items-center gap-1.5 ${hover === s.key ? "font-medium" : ""}`}
						onMouseEnter={() => setHover(s.key)}
						onMouseLeave={() => setHover(null)}
					>
						<span className="h-2 w-2 shrink-0 rounded-full" style={{ background: s.color }} />
						<span className="truncate text-text-secondary" title={s.label}>
							{s.label}
						</span>
						<span className="ml-auto font-mono text-text-primary">{s.value}</span>
						<span className="font-mono text-text-muted">
							{Math.round((s.value / total) * 100)}%
						</span>
					</div>
				))}
			</div>
		</div>
	);
}

// ---------------------------------------------------------------- SegmentBar
export type BarSegment = { key: string; label: string; value: number; color: string };

/** 水平分段条(有序刻度分布,如 CX 1→5):段间 2px 留白、段内直标(放得下才标)。 */
export function SegmentBar({
	segments,
	height = 26,
	testId,
}: {
	segments: BarSegment[];
	height?: number;
	testId?: string;
}) {
	const total = segments.reduce((a, s) => a + s.value, 0);
	if (!total) return <div className="p-3 text-xs text-text-muted">该窗口暂无数据。</div>;
	return (
		<div data-testid={testId}>
			<div className="flex w-full overflow-hidden rounded-md" style={{ height, gap: GAP }}>
				{segments
					.filter((s) => s.value > 0)
					.map((s) => {
						const pct = (s.value / total) * 100;
						return (
							<div
								key={s.key}
								title={`${s.label} · ${s.value}(${Math.round(pct)}%)`}
								className="flex items-center justify-center rounded-[3px] font-mono text-[10px] text-white"
								style={{ width: `${pct}%`, background: s.color, minWidth: 6 }}
							>
								{pct >= 9 ? s.value : ""}
							</div>
						);
					})}
			</div>
			<div className="flex flex-wrap gap-3 pt-1.5 text-xs">
				{segments.map((s) => (
					<span key={s.key} className="flex items-center gap-1.5 text-text-secondary">
						<span className="h-2 w-2 rounded-[2px]" style={{ background: s.color }} />
						{s.label}
						<span className="font-mono text-text-primary">{s.value}</span>
					</span>
				))}
			</div>
		</div>
	);
}
